import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Badge, Popover, Typography, Button, CircularProgress, Divider } from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { list_notifications_api, mark_notifications_read_api, respond_to_booking_api } from "../DAL/SchoolPortal/Tutors";
import useResilientAction from "../Hooks/useResilientAction";
import {
  accent, textPrimary, textSecondary, textMuted, font, border, surface, success, warning,
} from "../layouts/schoolPortal/schoolPortalTheme";

/**
 * Notifications, with the decision attached (task 9.19).
 *
 * WHY ACCEPT AND DECLINE LIVE HERE
 * A booking request expires if nobody answers, and to the student an expiry is
 * indistinguishable from being ignored. Making an instructor open a portal,
 * find the request and then answer it adds three chances to get distracted
 * between seeing it and doing it — at an airfield, between lessons, on a
 * phone. The decision belongs at the point of noticing.
 *
 * Every action goes through useResilientAction: optimistic so a slow link does
 * not look like a dead button, retried on network failure, and double-taps
 * swallowed so a struggling connection cannot send two accepts.
 */
const POLL_MS = 60000;

export default function NotificationBell() {
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { run, pending, error, clearError } = useResilientAction();

  const load = useCallback(async () => {
    try {
      const res = await list_notifications_api();
      if (res?.code === 200) setItems(res.notifications || []);
    } catch (_) {
      // Silent: a failed poll must not throw a banner at someone every minute
      // on a bad link. The unread count simply does not move.
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((i) => !i.read).length;

  const open = async (e) => {
    setAnchor(e.currentTarget);
    setLoading(true);
    await load();
    setLoading(false);
  };

  const close = () => {
    setAnchor(null);
    clearError();
    // Opening the panel does NOT clear anything, and neither does closing it.
    //
    // Marking on close still assumes that looking is the same as dealing with
    // it. An instructor glancing at the bell between lessons, on a phone, has
    // not answered anything — and the badge going to zero is precisely how the
    // one request they meant to come back to disappears. It stays until they
    // say so.
  };

  /** Explicit, per item. */
  const dismiss = (n) =>
    run(`${n.id}:dismiss`, () => mark_notifications_read_api([n.id]), {
      optimistic: () => setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: true } : i))),
      rollback: () => setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: false } : i))),
    });

  /** Explicit, all of them — for the morning after a busy week. */
  const dismissAll = () =>
    run("dismiss:all", () => mark_notifications_read_api(), {
      optimistic: () => setItems((prev) => prev.map((i) => ({ ...i, read: true }))),
      rollback: () => setItems((prev) => prev.map((i) => ({ ...i, read: unread > 0 ? false : i.read }))),
    });

  const respond = (n, decision) =>
    run(
      `${n.id}:${decision}`,
      () => respond_to_booking_api(n.data?.bookingId, decision),
      {
        // The row disappears immediately; put back only if the call fails.
        optimistic: () => setItems((prev) => prev.filter((i) => i.id !== n.id)),
        rollback: () => setItems((prev) => [n, ...prev.filter((i) => i.id !== n.id)]),
      }
    );

  return (
    <>
      <IconButton onClick={open} aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} sx={{ color: textSecondary }}>
        <Badge badgeContent={unread} color="error" max={9}>
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: { xs: 320, sm: 380 }, maxHeight: 480, borderRadius: "12px", border: `1px solid ${border}` } } }}
      >
        <Box sx={{ p: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: 14, color: textPrimary }}>
            Notifications{unread ? ` (${unread})` : ""}
          </Typography>
          {unread > 0 && (
            <Button size="small" onClick={dismissAll}
              sx={{ textTransform: "none", fontFamily: font, fontSize: 12, color: textSecondary, minWidth: 0 }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {error && (
          <Box sx={{ p: 1.5, background: "rgba(217,119,6,0.08)" }}>
            <Typography sx={{ fontFamily: font, fontSize: 12.5, color: warning }}>{error}</Typography>
          </Box>
        )}

        {loading && !items.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={22} sx={{ color: accent }} /></Box>
        ) : !items.length ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ fontFamily: font, fontSize: 13, color: textMuted }}>Nothing new.</Typography>
          </Box>
        ) : (
          items.map((n) => {
            const actionable = n.type === "booking_requested" && n.data?.bookingId;
            const busy = pending[`${n.id}:accept`] || pending[`${n.id}:decline`];
            return (
              <Box key={n.id} sx={{ p: 1.75, borderBottom: `1px solid ${border}`, background: n.read ? surface : "rgba(192,30,46,0.03)" }}>
                <Typography sx={{ fontFamily: font, fontSize: 13, color: textPrimary, lineHeight: 1.45 }}>
                  {n.text}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: 11, color: textMuted, mt: 0.5 }}>
                  {new Date(n.at).toLocaleString()}
                </Typography>

                {!n.read && !actionable && (
                  <Button size="small" onClick={() => dismiss(n)} disabled={pending[`${n.id}:dismiss`]}
                    sx={{ textTransform: "none", fontFamily: font, fontSize: 11.5, color: textMuted, p: 0, minWidth: 0, mt: 0.75 }}>
                    Dismiss
                  </Button>
                )}

                {actionable && (
                  <Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
                    <Button
                      size="small" disabled={busy}
                      onClick={() => respond(n, "accept")}
                      sx={{ textTransform: "none", fontFamily: font, fontSize: 12, fontWeight: 700, color: "#fff", background: success, px: 2, "&:hover": { background: success, filter: "brightness(0.95)" } }}
                    >
                      {busy ? "…" : "Accept"}
                    </Button>
                    <Button
                      size="small" disabled={busy}
                      onClick={() => respond(n, "decline")}
                      sx={{ textTransform: "none", fontFamily: font, fontSize: 12, color: textSecondary, border: `1px solid ${border}`, px: 2 }}
                    >
                      Decline
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Popover>
    </>
  );
}
